import { useCallback, useMemo } from 'react'

import { InputSelectControlField } from '@sequencehq/core-components'

import { usePricingEditorDomainContext } from 'common/drawers/PricingEditor/communication'
import {
  PricingEditorDomainInterface,
  Product
} from 'common/drawers/PricingEditor/domain'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useLoadAllItemsQuery } from '@sequencehq/api'
import invariant from 'tiny-invariant'

const productToOption =
  (ctx: PricingEditorDomainInterface['queries']['rawData']) =>
  (product: Product) => {
    return {
      label: product.name,
      value: product.id,
      /**
       * Disallow changing to a product that is already used in the current phase,
       * but allow reselecting the original product
       */
      disabled:
        ctx.configuration.productIdsUsedInPhase.includes(product.id) &&
        product.id !== ctx.initialData.product?.id
    }
  }

const useProductSelectField = () => {
  const pricingEditorContext = usePricingEditorDomainContext()

  const allProductsQuery = useLoadAllItemsQuery(
    dashboardv99990101Client.getProducts
  )

  const allProducts = useMemo(() => {
    return allProductsQuery.data?.pages.flatMap(page => page.items) ?? []
  }, [allProductsQuery.data])

  const value = useMemo(() => {
    return pricingEditorContext.queries.rawData.data.product.id
  }, [pricingEditorContext.queries.rawData.data.product.id])

  const options = useMemo(() => {
    const allOptions = Object.values(allProducts).map(product =>
      productToOption(pricingEditorContext.queries.rawData)(product)
    )

    const selectedOption = allOptions.filter(option => option.value === value)

    const availableOptions = allOptions.filter(
      option => !option.disabled && option.value !== value
    )

    const disabledOptions = allOptions.filter(option => option.disabled)

    return [...selectedOption, ...availableOptions, ...disabledOptions]
  }, [allProducts, pricingEditorContext.queries.rawData, value])

  const onChange = useCallback(
    (newProductId: string) => {
      const newProduct = allProducts.find(
        product => product.id === newProductId
      )

      invariant(newProduct, 'newProduct is undefined')

      pricingEditorContext.mutators.common.updateProduct(newProduct)

      pricingEditorContext.mutators.common.updatePricingEditorData({
        common: {
          productId: newProduct.id,
          name: newProduct.name
        }
      })
    },
    [pricingEditorContext.mutators.common, allProducts]
  )

  const showField = useMemo(() => {
    return pricingEditorContext.queries.rawData.configuration
      .showProductSelectField
  }, [
    pricingEditorContext.queries.rawData.configuration.showProductSelectField
  ])

  const validationErrors = useMemo(() => {
    const showValidationErrors =
      pricingEditorContext.queries.rawData.editor.showValidationErrors
    const productId =
      pricingEditorContext.queries.rawData.data.pricingEditorData.common
        .productId

    if (showValidationErrors && !productId) {
      return [{ message: 'This field is required' }]
    }

    return []
  }, [
    pricingEditorContext.queries.rawData.editor.showValidationErrors,
    pricingEditorContext.queries.rawData.data.pricingEditorData.common.productId
  ])

  return {
    value,
    options,
    onChange,
    showField,
    validationErrors,
    isLoading: allProductsQuery.isPending
  }
}

export const ProductSelectField = () => {
  const { value, options, onChange, showField, validationErrors, isLoading } =
    useProductSelectField()

  if (!showField) return null

  return (
    <InputSelectControlField
      key={value}
      label="Product"
      data-testid="common.product"
      value={value}
      options={options}
      onChange={onChange}
      placeholder={isLoading ? 'Loading products...' : 'Select a product...'}
      validationErrors={validationErrors}
      disabled={isLoading}
    />
  )
}
